import React, { useEffect, useState } from "react";
import { CoreTracking } from "../../../../models/CoreTracking";
import { useDispatch, useSelector } from "react-redux";
import { ClearRestoreAsmUserTracking, RestoreAsmUserTracking } from "../../../../store/TrackingOperationAction";
import ModalComponent from "../../../misc/ModalComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StatusCode from "../../../../util/StatusCode";
import Loading from "../../../misc/Loading";
import { RequestState } from "../../../../models/RequestState";
import { CoreAsmUserTrackingConflict } from "../../../../models/CoreTrackingOperation";
import AsmUserTrackingOperationsValidationResult from "./AsmUserTrackingOperationsValidationResult";
import CoreApiError from "../../../misc/CoreApiError";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../../store/store";



interface RestoreAsmUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const RestoreAsmUserTrackingWizard: React.FC<RestoreAsmUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["common", "schoolAdministration"]);
    const [wizardStep, setWizardStep] = useState<'confirm' | 'done'>('confirm');
    const restoreAsmUserTracking = useSelector<RootState, RequestState<CoreAsmUserTrackingConflict[]>>(state => state.trackingOperation.restoreAsmUserTracking)

    useEffect(() => {
        RestoreAsmUserTracking(props.tracking.trackingId, true)(dispatch);
        return () => ClearRestoreAsmUserTracking()(dispatch);
    }, [props, dispatch]);

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        RestoreAsmUserTracking(props.tracking.trackingId, false)(dispatch);
        setWizardStep('done');
    }

    const close = (success: boolean) => {
        setWizardStep('confirm');
        props.close(success);
    }

    switch (wizardStep) {
        case 'confirm': {
            if (restoreAsmUserTracking.code === StatusCode.COMPLETE) {
                if (restoreAsmUserTracking.data.length === 0)
                {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.asm.restore.title', { ns: 'schoolAdministration' })}
                        update={confirmInput}
                        confirm={t('action.confirm', { ns: 'common' })}
                        cancel={t('action.close', { ns: 'common' })}>
                        <p><FontAwesomeIcon title="OK" color="green" icon={['fas', 'check-circle']} /> {t('trackingOperations.asm.restore.noIssuesText', { ns: 'schoolAdministration' })}</p>
                    </ModalComponent>
                }

                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.restore.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}>
                    <p><FontAwesomeIcon title="ERROR" color="red" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.asm.restore.conflictsDetected', { ns: 'schoolAdministration' })}</p>
                    <AsmUserTrackingOperationsValidationResult asmId={props.tracking.sourceSystemId} conflicts={restoreAsmUserTracking.data} />
                </ModalComponent>;
            } else if (restoreAsmUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.restore.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}><CoreApiError requestState={restoreAsmUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.restore.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}>
                    <Loading />
                </ModalComponent>;
            }
        }
        case 'done': {
            if (restoreAsmUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.asm.restore.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.close', { ns: 'common' })}>{t('trackingOperations.asm.restore.complete', { ns: 'schoolAdministration' })}</ModalComponent>
            } else if (restoreAsmUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.restore.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}><CoreApiError requestState={restoreAsmUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.restore.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

export default RestoreAsmUserTrackingWizard;
